import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { muscleLabel } from 'components/play/constants';

// Ítem de la lista de grupos (md+): fila con barra dorada de selección a la izquierda.
function GroupItem({ label, count, selected, onClick }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1.1,
        borderRadius: '12px',
        cursor: 'pointer',
        position: 'relative',
        color: selected ? 'selected.fg' : 'text.secondary',
        backgroundColor: selected ? 'selected.bg' : 'transparent',
        transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1), color 200ms cubic-bezier(0.32,0.72,0,1)',
        '&:hover': { backgroundColor: selected ? 'selected.bg' : 'surface.hover', color: 'text.primary' },
        '&:active': { transform: 'scale(0.98)' },
        // Dorado SOLO como indicador pequeño de la selección.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '22%',
          bottom: '22%',
          width: 3,
          borderRadius: 999,
          backgroundColor: selected ? 'gold.main' : 'transparent',
          transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1)'
        }
      }}>
      <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'text.disabled' }}>
        {count}
      </Typography>
    </Box>
  );
}

GroupItem.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

/**
 * OTRIX Play — navegación de grupos musculares del catálogo (columna izquierda md+,
 * chips horizontales scrolleables en xs). Estilo biblioteca: lista limpia con contadores.
 */
function PlayCatalogGroupNav({ groups, active = '', onSelect }) {
  const { t } = useTranslation(['play']);

  return (
    <>
      {/* md+: lista vertical sticky */}
      <Stack
        spacing={0.25}
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'sticky',
          top: 88,
          p: 1,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'hairline.main',
          backgroundColor: 'surface.content',
          boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)'
        }}>
        {groups.map((g) => (
          <GroupItem
            key={g.value || 'all'}
            label={g.value ? muscleLabel(t, g.value) : t('browser.all', { ns: 'play' })}
            count={g.count}
            selected={active === g.value}
            onClick={() => onSelect(g.value)}
          />
        ))}
      </Stack>

      {/* xs-sm: chips horizontales */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          display: { xs: 'flex', md: 'none' },
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': { display: 'none' }
        }}>
        {groups.map((g) => (
          <Chip
            key={g.value || 'all'}
            label={`${g.value ? muscleLabel(t, g.value) : t('browser.all', { ns: 'play' })} · ${g.count}`}
            onClick={() => onSelect(g.value)}
            sx={{
              flex: 'none',
              fontWeight: 700,
              bgcolor: active === g.value ? 'selected.bg' : 'surface.overlay',
              color: active === g.value ? 'selected.fg' : 'text.secondary',
              border: '1px solid',
              borderColor: active === g.value ? 'gold.main' : 'hairline.main',
              '&:active': { transform: 'scale(0.97)' }
            }}
          />
        ))}
      </Stack>
    </>
  );
}

PlayCatalogGroupNav.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      count: PropTypes.number.isRequired
    })
  ).isRequired,
  active: PropTypes.string,
  onSelect: PropTypes.func.isRequired
};

export default PlayCatalogGroupNav;
